import request, { Method } from "../request";

export interface AIQuestionOption {
    jump: string | null;
    name: string;
    skip: string[];
    score: string;
    name_ar: string;
}

export interface AIQuestionerItem {
    id: number;
    order: number;
    type: string;
    mandatory: number;
    question: string;
    question_ar?: string;
    score?: number;
    imageUrl: string;
    options: AIQuestionOption[];
    aiSuggestedAnswer: AIQuestionOption | null;
    aiConfidence: number | null;
    image_mandatory: number;
}

export interface AIAnalysisResult {
    checkId: string | number;
    needsIdCheck: number;
    needsStickerCheck: number;
    needsVehicleCheck: number;
    continueAfterFakeId: number;
    stickerEditable: boolean;
    questioner: AIQuestionerItem[];
    gear: any[];
}

export interface DashboardInformation {
    checksToday: number | null;
    warningsGiven: number | null;
    fakeRiderIdsFound: number | null;
    wrongVehiclesFound: number | null;
    qualityScore: number | null;
    currentDailyGoal: number | null;
    kpiStatistics?: KpiStatistics | null;
    checksCurrentMonth: number | null;
    thisMonthGoals: number | null;
    currentFinalGoal: number | null;
}

export interface KpiStatistics {
    score: number | null;
    auditscore: number | null;
    accuracy: number | null;
    survey: number | null;
    assessment: number | null;
    attendance: number | null;
    total: number | null;
    scoreDate: string | null;
    shift_score: string | null;
}

export interface DailyGoalRequests {
    id: number | null;
    profiles_id: number | null;
    goal: number | null;
    date: string | null;
    approved: boolean;
    approved_at: number;
    created_at: number;
    updated_at: number;
    approved_by: number | null;
    modification_request: string | null;
    modification_response: string | null;
}

export interface ClockInData {
    clockIn: string | null;
    clockOut: string | null;
}

export interface Shift {
    totalSeconds: string;
    inShift: boolean;
    lastShift: string;
}

class PatrollerService {

    async getDashboardInformation() {
        return request<DashboardInformation>(`/t-patroller/dashboard`, Method.GET);
    }

    async getDailyGoalRequests() {
        return request<DailyGoalRequests[]>(`/t-patroller/get-daily-goal-requests`, Method.GET);
    }

    async createDailyGoalRequest(date: string, goal: string, note: string) {
        return request<any>(`/t-patroller/create-daily-goal-request`, Method.POST, {
            data: {
                date: date,
                daily_goal: goal,
                note: note,
            },
        });
    }

    async isRiderReadyForCheck(riderId: number|string) {
        return request<any>(`/t-patroller/rider-ready-for-check`, Method.POST, {
            data: { rider_id: riderId },
        });
    }

    async listHistory(riderId: number|string) {
        return request<any>(`/t-patroller/history`, Method.POST, {
            data: { rider_id: riderId },
        });
    }

    async latestCheckDate(riderId: number|string) {
        return request<any>(`/t-patroller/get-latest-check-date`, Method.POST, {
            data: { rider_id: riderId },
        });
    }

    async getCheckForRider(riderId: number|string) {
        let check = null;
        if (navigator.geolocation){
            navigator.geolocation.getCurrentPosition(function (position){
                let latitude = position.coords.latitude;
                let longitude = position.coords.longitude;

                check = request<any>(`/t-patroller/get-check-for-rider`, Method.POST, {
                    data: { rider_id: riderId, latitude: latitude, longitude: longitude },
                });
            }, function (error) {
                check = request<any>(`/t-patroller/get-check-for-rider`, Method.POST, {
                    data: { rider_id: riderId, latitude: null, longitude: null },
                });
            });
        }
        else {
            check = request<any>(`/t-patroller/get-check-for-rider`, Method.POST, {
                data: { rider_id: riderId, latitude: null, longitude: null },
            });
        }

        while (check === null){
            await new Promise(r => setTimeout(r, 1000));
        }

        return check;
    }

    setCheckForRider(riderId: number|string, checkId: number|string) {
        localStorage.setItem(`checkId|${riderId}`, checkId.toString());
    }

    getCheckForRiderFromLocalStorage(riderId: number|string) {
        return localStorage.getItem(`checkId|${riderId}`);
    }

    saveIdStepCheckForRider(riderId: number|string, isIdValid: boolean, idPicture: any) {
        let checkId = this.getCheckForRiderFromLocalStorage(riderId);
        if (!checkId){
            return;
        }
        console.log(isIdValid);
        return request<any>(`/t-patroller/check-rider-id`, Method.POST, {
            data: {
                check_id: checkId,
                is_id_valid: isIdValid ? '1' : '0',
                id_picture: idPicture,
            },
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    /*saveStickerStepCheckForRider(riderId: number|string, isStickerValid: boolean, stickerPicture: any) {
        let checkId = this.getCheckForRiderFromLocalStorage(riderId);
        if (!checkId){
            return;
        }
        console.log(isStickerValid);
        return request<any>(`/t-patroller/check-rider-id`, Method.POST, {
            data: {
                check_id: checkId,
                is_sticker_valid: isStickerValid ? '1' : '0',
                sticker_picture: stickerPicture,
            },
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }*/

    saveVehicleStepCheckForRider(riderId: number|string, isLicensePlateValid: boolean, licensePlateNumber: string|null, licensePlatePicture: any, validLicensePlatePicture: any) {
        let checkId = this.getCheckForRiderFromLocalStorage(riderId);
        if (!checkId){
            return;
        }
        return request<any>(`/t-patroller/check-license-plate`, Method.POST, {
            data: {
                check_id: checkId,
                is_license_plate_valid: isLicensePlateValid ? '1' : '0',
                license_plate_number: licensePlateNumber,
                license_plate_picture: licensePlatePicture,
                correct_license_plate_picture: validLicensePlatePicture,
            },
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    saveStickerStepCheckForRider(riderId: number|string, isStickerValid: boolean, isMissing: boolean, stickerNumber: string|null, stickerPicture: any, validstickerPicture: any) {
        let checkId = this.getCheckForRiderFromLocalStorage(riderId);
        if (!checkId){
            return;
        }
        return request<any>(`/t-patroller/check-sticker`, Method.POST, {
            data: {
                check_id: checkId,
                is_sticker_valid: isStickerValid ? '1' : '0',
                sticker_number: stickerNumber,
                sticker_picture: stickerPicture,
                correct_sticker_picture: validstickerPicture,
                is_missing: isMissing ? '1' : '0',
            },
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    saveGearCheckData(riderId: number|string, gearCheckData: any) {
        let checkId = this.getCheckForRiderFromLocalStorage(riderId);
        if (!checkId){
            return;
        }
        const formData = new FormData();
        let dataToSend = {
            check_id: checkId,
            gear_check_data: gearCheckData,
        };
        //eslint-disable-next-line
        for (let key in dataToSend) {
            // @ts-ignore
            if (typeof dataToSend[key] === 'object') {
                // @ts-ignore
                for (let subKey in dataToSend[key]) {
                    // @ts-ignore
                    if (Array.isArray(dataToSend[key][subKey])) {
                        // If the value is an array, append each element separately
                        // @ts-ignore
                        dataToSend[key][subKey].forEach((item:any, index:any) => {
                            formData.append(`${key}[${subKey}][${index}]`, item);
                        });
                        // @ts-ignore
                    } else if(typeof dataToSend[key][subKey] === 'object' && subKey !== 'picture') {
                        // @ts-ignore
                        for (let subSubKey in dataToSend[key][subKey]) {
                            // @ts-ignore
                            if (Array.isArray(dataToSend[key][subKey][subSubKey])) {
                                // If the value is an array, append each element separately
                                // @ts-ignore
                                dataToSend[key][subKey][subSubKey].forEach((item:any, index:any) => {
                                    formData.append(`${key}[${subKey}][${subSubKey}][${index}]`, item);
                                });
                                // @ts-ignore
                            } else if(typeof dataToSend[key][subKey][subSubKey] === 'object' && subSubKey !== 'picture'){
                                //@ts-ignore
                                for (let subSubSubKey in dataToSend[key][subKey][subSubKey]) {
                                    // @ts-ignore
                                    if (Array.isArray(dataToSend[key][subKey][subSubKey][subSubSubKey])) {
                                        // If the value is an array, append each element separately
                                        // @ts-ignore
                                        dataToSend[key][subKey][subSubKey][subSubSubKey].forEach((item:any, index:any) => {
                                            formData.append(`${key}[${subKey}][${subSubKey}][${subSubSubKey}][${index}]`, item);
                                        });
                                        // @ts-ignore
                                    }
                                    else {
                                        // @ts-ignore
                                        formData.append(`${key}[${subKey}][${subSubKey}][${subSubSubKey}]`, dataToSend[key][subKey][subSubKey][subSubSubKey]);
                                    }
                                }
                            }
                            else {
                                // @ts-ignore
                                formData.append(`${key}[${subKey}][${subSubKey}]`, dataToSend[key][subKey][subSubKey]);
                            }
                        }
                    }else {
                        // @ts-ignore
                        formData.append(`${key}[${subKey}]`, dataToSend[key][subKey]);
                    }
                }
            } else {
                // @ts-ignore
                formData.append(key, dataToSend[key]);
            }
        }
        return request<any>(`/t-patroller/save-gears-check-data`, Method.POST, {
            data: {
                check_id: checkId,
                gear_check_data: gearCheckData,
            },
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    saveQuestionerData(riderId: number|string, questionerData: any) {
        const checkId = this.getCheckForRiderFromLocalStorage(riderId);
        if (!checkId) {
            return;
        }

        const formData = new FormData();
        formData.append('check_id', checkId);

        questionerData.forEach((item: any, index: number) => {
            const q = item.questionerQuestion ?? {};

            formData.append(`questioner_data[${index}][questionerQuestionId]`, item.questionerQuestionId ?? '');
            formData.append(`questioner_data[${index}][score]`,                item.score ?? '0');
            formData.append(`questioner_data[${index}][skipped]`,              item.skipped ? '1' : '0');
            formData.append(`questioner_data[${index}][questionerQuestion][id]`,             q.id             ?? '');
            formData.append(`questioner_data[${index}][questionerQuestion][category]`,        q.category       ?? '');
            formData.append(`questioner_data[${index}][questionerQuestion][question]`,        q.question       ?? '');
            formData.append(`questioner_data[${index}][questionerQuestion][question_ar]`,     q.question_ar    ?? '');
            formData.append(`questioner_data[${index}][questionerQuestion][type]`,            q.type           ?? '');
            formData.append(`questioner_data[${index}][questionerQuestion][mandatory]`,       q.mandatory      ?? '');
            formData.append(`questioner_data[${index}][questionerQuestion][order]`,           q.order          ?? '');
            formData.append(`questioner_data[${index}][questionerQuestion][image_mandatory]`, q.image_mandatory ?? '0');
            formData.append(`questioner_data[${index}][questionerQuestion][score]`,           q.score          ?? '0');

            if (Array.isArray(item.answer)) {
                item.answer.forEach((ans: string, ansIndex: number) => {
                    formData.append(`questioner_data[${index}][answer][${ansIndex}]`, ans);
                });
            } else {
                formData.append(`questioner_data[${index}][answer]`, item.answer ?? '');
            }

            if (item.imageUrl instanceof File) {
                formData.append(`questioner_data[${index}][imageUrl]`, item.imageUrl);
            }
        });

        return request<any>(`/t-patroller/save-questioner-data`, Method.POST, {
            data: formData,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    getNonTalabatGears() {
        return request<any>(`/t-patroller/get-non-talabat-products`, Method.GET);
    }

    getQuestionerQuestions(vehicle?: number | string) {
        const qs = vehicle != null ? `?vehicle=${encodeURIComponent(String(vehicle))}` : "";
        return request<any>(`/t-patroller/get-questioner-questions${qs}`, Method.GET);
    }


    getQuestionerCount(vehicle?: number | string) {
        const qs = vehicle != null ? `?vehicle=${encodeURIComponent(String(vehicle))}` : "";
        return request<any>(`/t-patroller/get-question-number${qs}`, Method.GET);
    }

    finishCheckForRider(riderId: number|string) {
        let checkId = this.getCheckForRiderFromLocalStorage(riderId);
        if (!checkId){
            return;
        }
        return request<any>(`/t-patroller/finish-check`, Method.POST, {
            data: {
                check_id: checkId,
            },
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    analyzeRiderPhoto(riderId: number | string, photo: File): Promise<AIAnalysisResult> {
        const formData = new FormData();
        formData.append('rider_id', riderId.toString());
        formData.append('rider_photo', photo);
        return request<AIAnalysisResult>(`/t-patroller/analyze-rider-photo`, Method.POST, {
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    uploadAiPhoto(
        photo: File,
        meta?: { latitude?: number | null; longitude?: number | null; capturedAt?: string },
    ): Promise<{ token: string }> {
        const formData = new FormData();
        formData.append('rider_photo', photo);

        if (meta?.latitude != null)  formData.append("latitude", meta.latitude.toString());
        if (meta?.longitude != null) formData.append("longitude", meta.longitude.toString());
        if (meta?.capturedAt)        formData.append("captured_at", meta.capturedAt);

        return request<{ token: string }>(`/t-patroller/upload-ai-photo`, Method.POST, {
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    pollAiPhotoStatus(token: string): Promise<{ status: string; questioner?: any[]; gear?: any[] }> {
        return request<{ status: string; questioner?: any[]; gear?: any[] }>(
            `/t-patroller/ai-photo-status/${token}`,
            Method.GET
        );
    }

    startAiCheck(riderId: number | string, uploadToken: string): Promise<AIAnalysisResult & { aiReady: boolean }> {
        const formData = new FormData();
        formData.append('rider_id', riderId.toString());
        formData.append('upload_token', uploadToken);
        return request<AIAnalysisResult & { aiReady: boolean }>(`/t-patroller/start-ai-check`, Method.POST, {
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    submitAICheck(riderId: number | string, data: {
        checkId: number | string;
        fakeId: boolean;
        latitude: number | null;
        longitude: number | null;
        idPicture?: File;
    }): Promise<any> {
        const formData = new FormData();
        formData.append('check_id', data.checkId.toString());
        formData.append('fake_id', data.fakeId ? '1' : '0');
        if (data.latitude !== null) formData.append('latitude', data.latitude.toString());
        if (data.longitude !== null) formData.append('longitude', data.longitude.toString());
        if (data.idPicture) formData.append('id_picture', data.idPicture);
        return request<any>(`/t-patroller/submit-ai-check`, Method.POST, {
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    async savePatrollerLocation(latitude: number, longitude: number) {
        let currentDateTime = new Date();
        let formattedDateTime = currentDateTime.toISOString().slice(0, 19);
        formattedDateTime = formattedDateTime.replace('T', ' ');
        formattedDateTime = formattedDateTime.replace('Z', '');

        return request<any>(`/t-patroller/save-gps-location`, Method.POST, {
            data: {
                latitude: latitude,
                longitude: longitude,
                timestamp: formattedDateTime
            },
        });
    }

    async getClockInData() {
        return request<ClockInData>(`/t-patroller/clock-in`, Method.POST);
    }

    async getClockOutData() {
        return request<ClockInData>(`/t-patroller/clock-out`, Method.POST);
    }

    async getSift() {
        return request<Shift>(`/t-patroller/shift`, Method.GET);
    }

    async saveFakeIdNote(riderId: number | string, note: string, images: File[]) {
        const checkId = this.getCheckForRiderFromLocalStorage(riderId);
        if (!checkId) return;

        const formData = new FormData();
        formData.append("check_id", checkId);
        formData.append("note", note);

        images.forEach((file, index) => {
            formData.append(`images[${index}]`, file);
        });

        return request<any>(`/t-patroller/save-fake-id-note`, Method.POST, {
            data: formData,
            headers: { "Content-Type": "multipart/form-data" },
        });
    }
}


export const patrollerService= new PatrollerService();
